import React, { useEffect, useRef, useState } from "react";
import { axiosPublic } from "../api/axiosCustom";
import { useNavigate } from "react-router-dom";
import useZustandStore from "../zustandStore/useZustandStore";
import toast from "react-hot-toast";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import ButtonLoader from "./Icons/ButtonLoader";
import LoginAPIs from "../apiRoutes/Login";
import { GoogleLogin } from "@react-oauth/google";


const Login = () => {
  const [otp, setOtp] = useState("");
  const [phone, setPhone] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPhoneLogin, setShowPhoneLogin] = useState(false);
  const otpInputRef = useRef(null);
  const setCurrentUser = useZustandStore((val) => val?.setCurrentUser);
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();

  //  When click enter after adding phone number
  const handleKeyDownPhone = (e) => {
    if (e.key === "Enter" && phone.length === 10) {
      handleGetOTP();
    }
  };

  //  When click enter after entering OTP
  const handleKeyDownOtp = (e) => {
    if (e.key === "Enter" && otp.length === 6) {
      handleVeryOtpAndLogin();
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (value.length <= 10) setPhone(value);
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (value.length <= 6) setOtp(value);
  };

  // API Call to sent OPT
  const handleGetOTP = async () => {
    if (phone.length === 10) {
      try {
        setIsLoading(true);
        const payload = {
          phone_number: phone,
          country_code: "+91",
        };
        const { response } = await LoginAPIs.sendOtp({ axiosPublic, payload });
        const { data } = response;
        if (data) {
          setOtpSent(true);
          setIsLoading(false);
          setVerificationId(data?.verification_id);
          toast.success(
            `OTP send to phone +91 xxxxxxxx${phone?.substr(8, 10)}.`
          );
        }
      } catch (error) {
        setOtpSent(false);
        setIsLoading(false);
        setVerificationId("");
        toast.error(error?.response.data?.error_message || error?.message);
      }
    }
  };

  const handleValidateUserAndStoreDetails = async () => {
    try {
      const { response } = await LoginAPIs.getProfile({ axiosPrivate });
      const { data } = response;
      const user = data?.data?.user;
      if (user) {
        setIsLoading(false);
        setCurrentUser(user);
        toast.success(`Hey ${user?.name}, good to see you!`, {
          icon: "👋",
          duration: 5000,
        });
        navigate("/", { replace: true });
      }
    } catch (error) {
      setIsLoading(false);
      toast.error(error?.response?.data?.error_message || error?.message);
    }
  };

  const handleVeryOtpAndLogin = async () => {
    if (otp.length === 6) {
      try {
        setIsLoading(true);
        const payload = {
          otp,
          phone_number: phone,
          country_code: "+91",
          verification_id: verificationId,
        };
        const { response } = await LoginAPIs.verifyOtp({ axiosPublic, payload });
        const { data } = response;
        if (data?.data) {
          handleValidateUserAndStoreDetails();
        }
      } catch (error) {
        setIsLoading(false);
        toast.error(error?.response.data?.error_message || error?.message);
      }
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    try {
      setIsLoading(true);
      const payload = {
        credential: credentialResponse.credential,
      };
      const { response } = await LoginAPIs.googleLogin({
        axiosPublic,
        payload,
      });
      const { data } = response;
      if (data?.data) {
        handleValidateUserAndStoreDetails();
      }
    } catch (error) {
      setIsLoading(false);
      toast.error(
        error?.response?.data?.error_message ||
        error?.message ||
        "Google Login Failed"
      );
    }
  };

  const handleGoogleLoginError = () => {
    toast.error("Google Login Failed");
  };

  useEffect(() => {
    if (otpSent && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [otpSent]);

  return (
    <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-50 p-6">
      <div className="w-full max-w-md">
        {/* Logo & Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <img
              src="/static/images/seekho-logo.png"
              alt="Logo"
              className="h-14 w-14"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <span className="text-3xl font-bold bg-gradient-to-r from-[#5E30BF] to-[#AF30BF] bg-clip-text text-transparent">
              Seekho
            </span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-3">
            Welcome back
          </h2>
          <p className="text-gray-500 text-lg mb-2">Sign in to continue to your account</p>
          <p className="text-gray-400 text-sm">Manage your ads, track performance, and grow your business</p>
        </div>

        {/* Google Login - Primary Action */}
        <div className="mb-8 flex justify-center items-center">
          <div style={{ transform: 'scale(1.2)', transformOrigin: 'center' }}>
            <GoogleLogin
              onSuccess={handleGoogleLoginSuccess}
              onError={handleGoogleLoginError}
              theme="outline"
              size="large"
              shape="pill"
              text="continue_with"
              logo_alignment="center"
              width="400"
            />
          </div>
        </div>

        {/* Phone Login Toggle */}
        {!showPhoneLogin ? (
          <div className="text-center">
            <button
              onClick={() => setShowPhoneLogin(true)}
              className="text-sm text-gray-500 hover:text-primaryPurple transition-colors inline-flex items-center gap-2"
            >
              <span>Continue with phone instead</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        ) : (
          <>
            {/* Divider */}
            <div className="flex items-center my-6">
              <div className="flex-grow border-t border-gray-200"></div>
              <span className="mx-4 text-gray-400 text-sm font-medium">or</span>
              <div className="flex-grow border-t border-gray-200"></div>
            </div>

            {/* Phone Login Form */}
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">
                    +91
                  </span>
                  <input
                    type="tel"
                    placeholder="Enter 10-digit number"
                    value={phone}
                    onChange={handlePhoneChange}
                    onKeyDown={handleKeyDownPhone}
                    className="w-full pl-14 pr-4 py-3.5 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primaryPurple/20 focus:border-primaryPurple transition-all text-gray-800 placeholder:text-gray-400"
                  />
                </div>
              </div>

              {!otpSent ? (
                <button
                  onClick={handleGetOTP}
                  disabled={phone.length !== 10 || isLoading}
                  className={`w-full py-3.5 rounded-xl text-white font-medium transition-all duration-200 bg-gradient-to-r from-[#5E30BF] to-[#AF30BF] ${phone.length === 10 && !isLoading
                    ? "hover:shadow-lg hover:shadow-primaryPurple/30 hover:scale-[1.01] active:scale-[0.99]"
                    : "opacity-40 cursor-not-allowed"
                    }`}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <ButtonLoader />
                      <span>Sending OTP...</span>
                    </div>
                  ) : (
                    "Send OTP"
                  )}
                </button>
              ) : (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enter OTP
                    </label>
                    <input
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={otp}
                      ref={otpInputRef}
                      onChange={handleOtpChange}
                      onKeyDown={handleKeyDownOtp}
                      className="w-full px-4 py-3.5 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primaryPurple/20 focus:border-primaryPurple transition-all text-gray-800 placeholder:text-gray-400 text-center tracking-[0.5em] font-medium"
                      maxLength={6}
                    />
                    <p className="mt-2 text-xs text-gray-400 text-center">
                      OTP sent to +91 ******{phone?.substr(6, 4)}
                    </p>
                  </div>
                  <button
                    onClick={handleVeryOtpAndLogin}
                    disabled={otp.length !== 6 || isLoading}
                    className={`w-full py-3.5 rounded-xl text-white font-medium transition-all duration-200 bg-gradient-to-r from-[#5E30BF] to-[#AF30BF] ${otp.length === 6 && !isLoading
                      ? "hover:shadow-lg hover:shadow-primaryPurple/30 hover:scale-[1.01] active:scale-[0.99]"
                      : "opacity-40 cursor-not-allowed"
                      }`}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <ButtonLoader />
                        <span>Verifying...</span>
                      </div>
                    ) : (
                      "Verify & Login"
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setOtpSent(false);
                      setOtp("");
                    }}
                    className="w-full text-sm text-gray-500 hover:text-primaryPurple transition-colors"
                  >
                    Change phone number
                  </button>
                </div>
              )}

              <button
                onClick={() => {
                  setShowPhoneLogin(false);
                  setOtpSent(false);
                  setOtp("");
                  setPhone("");
                }}
                className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                Back to Google login
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Login;
